/**
 * Production-grade streaming message component
 *
 * Two-phase rendering:
 * 1. Streaming: Plain text with direct DOM updates (fast, no React overhead)
 * 2. Complete: Rich markdown rendering (once, when done)
 *
 * This matches ChatGPT/Claude production behavior.
 */

import React from 'react';
import { useHistory } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import InlineTodoList from './InlineTodoList';
import ChartDisplay from './ChartDisplay';
import NetworkTopologyDisplay from './NetworkTopologyDisplay';
import './StreamingMessage.css';

/**
 * Determine whether a URL is a portal-internal deeplink (relative path).
 * Used to open performance/inventory pages in the same tab instead of a new one.
 */
function isInternalDeeplink(href) {
    if (!href) return false;
    // Relative paths like /monitoring/performance or /monitoring/inventory
    return href.startsWith('/') && !href.startsWith('//');
}

const StreamingMessage = ({message, elementRef}) => {
    const history = useHistory();
    // Message is streaming if explicitly marked or if finalResponse exists but not done
    const showAsComplete = !message.isStreaming && message.finalResponse;

    if (!showAsComplete) {
        // PHASE 1: STREAMING - Plain text with direct DOM updates + thinking indicator
        return (
            <div className="streaming-message">
                {/* ✅ TODO TRACKER: Show during streaming */}
                {message.todos && message.todos.length > 0 && (
                    <InlineTodoList todos={message.todos}/>
                )}

                <div
                    ref={elementRef}
                    className="streaming-text"
                    aria-live="polite"
                />

                {/* ✅ THINKING INDICATOR: Show throughout entire streaming process */}
                {message.isStreaming && (
                    <span className="thinking-indicator">
            <span className="dot"></span>
            <span className="dot"></span>
            <span className="dot"></span>
          </span>
                )}
            </div>
        );
    }

    // PHASE 2: COMPLETE - Rich markdown rendering
    return (
        <div className="complete-message">
            {/* ✅ TODO TRACKER: Show when complete */}
            {message.todos && message.todos.length > 0 && (
                <InlineTodoList todos={message.todos}/>
            )}

            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    // Custom code block styling
                    code({node, inline, className, children, ...props}) {
                        return inline ? (
                            <code className="inline-code" {...props}>
                                {children}
                            </code>
                        ) : (
                            <pre className="code-block">
                <code className={className} {...props}>
                  {children}
                </code>
              </pre>
                        );
                    },
                    // Wrap tables so they scroll horizontally instead of overflowing
                    table({node, children, ...props}) {
                        return (
                            <div style={{ overflowX: 'auto', width: '100%' }}>
                                <table {...props}>{children}</table>
                            </div>
                        );
                    },
                    // Deeplinks (relative paths like /monitoring/performance) open
                    // in the same tab; all other links open in a new tab.
                    a({node, children, href, ...props}) {
                        if (isInternalDeeplink(href)) {
                            return (
                                <a
                                    {...props}
                                    href={href}
                                    className="agent-deeplink"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        // Client-side route navigation — keeps the chat's
                                        // React state (and this conversation) intact
                                        // instead of a full page reload.
                                        history.push(href);
                                    }}
                                >
                                    {children}
                                </a>
                            );
                        }
                        return (
                            <a {...props} href={href} target="_blank" rel="noopener noreferrer">
                                {children}
                            </a>
                        );
                    }
                }}
            >
                {message.finalResponse || ''}
            </ReactMarkdown>

            {/* Inline chart emitted by RenderChart tool */}
            {message.chartData && (
                <ChartDisplay chartData={message.chartData} />
            )}

            {/* Inline network topology diagram emitted by RenderTopology tool */}
            {message.topologyData && (
                <NetworkTopologyDisplay topologyData={message.topologyData} />
            )}

            {message.queryTime && (
                <div className="message-timing">
                    ⚡ {message.queryTime.toFixed(2)}s
                </div>
            )}
        </div>
    );
};

export default StreamingMessage;
